<template>
	<div class="course-request-form-wrap">
		<template v-if="!!response && warningMessage.length === 0">
			<br />
			<div class="row" style="margin-left: 0; margin-right: 0;">
				
				<div class="col-xs-12 alert" :class="{'alert-danger': response.error, 'alert-success': !response.error}">
					<template v-if="response.error">
						<p v-for="(message, messageIndex) in response.messages[0].VALIDATIONS" :key="messageIndex">{{ message.REASON }}</p>
					</template>
					<template v-else>
						<p v-for="(message, messageIndex) in response.messages" :key="messageIndex">{{ message }}</p>
					</template>
				</div>
			</div>
		</template>
		<template v-if="!!selectedStudent">
			<div v-if="required.length === 0 && elective.length === 0 && mandatory.length === 0">
				<p>{{ $t('MAIN_CONTENT.CourseRequestForms.selectCourseRequestFormToComplete') }}</p>
				<div class="row">
					<div class="selector-wrap col-md-10 col-lg-6 col-md-offset-1 col-lg-offset-2">
						<div class="row">
							<div class="col-sm-12 col-md-10">
								<multiselect v-model="selectedCourseForm"
									:options="courseRequestList"
									track-by="FORM_ID"
									style="margin-top: 12px"
									:custom-label="selectedCourseFormText"
									placeholder="Select a Course"
									v-validate="'required'"
									:allow-empty="false"
									:preselectFirst="true"
									:searchable="false"
									deselectLabel=""
								>
								</multiselect>
							</div>
						</div>
						<div class="row">
							<div class="col-xs-12 col-md-8 col-lg-8 col-md-offset-1">
								<button type="button"
												class="btn btn-primary btn-block"
												:disabled="Object.keys(selectedCourseForm).length <= 0"
												@click="loadCoursesForGrade">
									{{ $t('MAIN_CONTENT.CourseRequestForms.fillOutCourseRequestForm') }}
								</button>
							</div>
						</div>
					</div>
				</div>

				<div class="row">
					<div class="col-sm-12">
						<div class="instructions alert alert-info" style="display: inline-flex" v-if="!courseSelectionFrontPage">
							<ol>
								<li v-for="(step, index) in stepCount" :key="`steps_${index}`">
									{{ $t(`MAIN_CONTENT.CourseRequestForms.courseRequestFormInstructionStep${step}`) }}
								</li>
							</ol>
						</div>
						<div class="instructions alert alert-info" style="display: inline-flex" v-else v-html="courseSelectionFrontPage" />
					</div>
				</div>
			</div>

			<template v-if="required.length > 0 || mandatory.length > 0 || elective.length > 0">
				<!-- Info/Warning banner, can't save due to too many credits -->
				<div class="alert alert-warning" role="alertdialog" v-if="warningMessage.length > 0">
					{{ warningMessage }}
				</div>
				<!-- Total credits selected start -->
				<div class="credits-total">
					{{ $t('MAIN_CONTENT.CourseRequestForms.creditsWithColon', { count: creditCount } ) }}
				</div>
				<!-- Total credits selected end -->

				<!-- Required start -->
				<template v-if="required.length > 0">
					<h2>{{ $t('MAIN_CONTENT.CourseRequestForms.requiredCourses') }}</h2>
					<p>{{ selectedCourseForm.REQUIRED_DESC }}</p>
					<required :departments="required" :selectedCourseForm="selectedCourseForm" @dataChanged="setChangedData"></required>
				</template>
				<!-- Required end -->

				<!-- Mandatory start -->
				<template v-if="mandatory.length > 0">
					<h2>{{ $t('MAIN_CONTENT.CourseRequestForms.mandatoryCourses') }}</h2>
					<p>{{ selectedCourseForm.MANDATORY_DESC }}</p>
					<template v-for="(department, departmentIndex) in mandatory">
						<div class="panel panel-default pointer"
								:key="`department_${department.DEPARTMENT_ID}_${departmentIndex}`">
							<div class="panel-heading dark clearfix" @click="department.SHOW = !department.SHOW; valTest++;">
								<h3 class="panel-title">
									{{ department.DEPARTMENT_DESC }}
								</h3>
							</div>
							<mandatory :description="department.DEPARTMENT_DESC"
												:department="department"
												:key="`mandatory_${departmentIndex}`">
							</mandatory>
						</div>
					</template>
				</template>
				<!-- Mandatory end -->

				<!-- Elective start -->
				<template v-if="elective.length > 0">
					<h2>{{ $t('MAIN_CONTENT.CourseRequestForms.electiveCourses') }}</h2>
					<p>{{ selectedCourseForm.ELECTIVE_DESC }}</p>
					<template v-for="(department, departmentIndex) in elective">
						<div class="panel panel-default pointer"
								:key="`department_${department.DEPARTMENT_ID}_${departmentIndex}`">
							<div class="panel-heading dark clearfix" @click="department.SHOW = !department.SHOW; valTest++;">
								<h3 class="panel-title">
									{{ department.DEPARTMENT_DESC }}
								</h3>
							</div>
							<elective :department="department"
									v-show="department.SHOW"
									:key="`elective_${valTest}`"
									style="cursor: default;"
									@dataChanged="setChangedData"
									:selectedCourseForm="selectedCourseForm">
							</elective>
						</div>
					</template>
					<template v-if="selectedAlts.length > 1 && selectedCourseForm.USE_ALTERNATES_FLG === 'Y'">
						<div class="panel panel-default">
							<div class="panel-heading light clearfix">
								<h3 class="panel-title">
									{{ $t('MAIN_CONTENT.CourseRequestForms.rankAlternates') }} <br/>
									<small>
										{{ $t('MAIN_CONTENT.CourseRequestForms.rankAlternatesInstructions') }}
									</small>
								</h3>
							</div>
							<ul class="list-group">
								<Draggable v-model="selectedAlts" class="clickable" draggable=".drag-item">
									<li v-for="(altCourse,index) in selectedAlts" :key="`altRank${index}`" class="list-group-item drag-item">
										<img class="draghandle" style="cursor: move; float: left; margin-top: -3px;" src="/Sapphire/images/icons/move_up_down.gif" title="Drag Up or Down to reorder columns." alt="Drag">
										<span class="rank badge pull-left">
											{{ $t('MAIN_CONTENT.CourseRequestForms.rankAlternatesRank', { rank: index + 1 } ) }}
										</span> 
										{{ $t('MAIN_CONTENT.CourseRequestForms.rankAlternateCouses', { courseid: altCourse.COURSE_ID, coursedescription: altCourse.COURSE_DESC } ) }}
										<input type="hidden" class="weight" :input="altCourse.ALTERNATE_WEIGHT = (selectedAlts.length - index)" :value="(selectedAlts.length - index)" />
										<span class="pull-right">
											{{ $t('MAIN_CONTENT.CourseRequestForms.rankAlternatesCredits', { credit: altCourse.CREDITS } ) }}
										</span>
									</li>
								</Draggable>
							</ul>
						</div>
					</template>
				</template>
				<!-- Elective end -->

				<template v-if="required.length > 0 || mandatory.length > 0 || elective.length > 0">
				<!-- Info/Warning banner, can't save due to too many credits -->
				<div class="alert alert-warning" role="alertdialog" v-if="warningMessage.length > 0">
					{{ warningMessage }}
				</div>
				<!-- Total credits selected start -->
				<div class="credits-total">
					{{ $t('MAIN_CONTENT.CourseRequestForms.creditsWithColon', { count: creditCount } ) }}</div>
				<!-- Total credits selected end -->

				<!-- Mandatory end -->
				</template>
				<button class="btn btn-primary pull-right" @click="saveCourseRequestForm" :disabled="!!warningMessage">
					{{ $t('MAIN_CONTENT.commonContent.submitButton') }}
				</button>
			</template>

			<!-- Modal -->
			<modal :modal-title="modalTitle" :show="showModal" dialog-size="sm" name="courseRequestModal" @close="closeModal()">
				<template slot-scope="modalProps">
					<p class="text-center text-muted" v-for="(message, messageIndex) in modalContent" :key="messageIndex">
						{{ message }}
					</p>
				</template>
				<template slot="footer">
					<button type="button" class="btn btn-transparent pull-right" @click="closeModal()">
						{{ $t('MAIN_CONTENT.commonContent.closeButton') }}
					</button>
				</template>
			</modal>
		</template>
		<template v-else>
			{{ $t('MAIN_CONTENT.commonContent.noStudentHasBeenSelectedWithPeriod') }}
		</template>
		<form ref="printForm" action="/PortalAPI/index.cfm/api/v1/course-request-forms/form/print" target="printFrame" method="POST">
			<input type="hidden" name="student_rid" :value="selectedStudent.STUDENT_RID" />
			<input type="hidden" name="form_id" :value="selectedCourseForm.FORM_ID" />
			<input type="hidden" name="token" :value="$store.state.accessToken" />
			<input type="hidden" name="client_id" :value="$store.state.clientID" />
		</form>
		<iframe name="printFrame" ref="printFrame" style="display:hidden" class="hidden" />
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import Multiselect from 'vue-multiselect';

	import CourseRequest from '../api/CourseRequest.js';
	import Mandatory from './CourseRequestForms/Mandatory.vue';
	import Required from './CourseRequestForms/Required.vue';
	import Elective from './CourseRequestForms/Elective.vue';

	import Modal from 'COMMONS/shared/Modal/Modal.vue';
	import Draggable from "vuedraggable";

	export default {
		data() {
			return {
				buttons: ['save'],
				courseGroups: [],
				stepCount: ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'],
				selectedCourseForm: {},
				mandatory: [],
				elective: [],
				required: [],
				dataAltered: false,
				showModal: false,
				valTest: 0,
				courseRequestList: [],
				modalTitle: 'Sapphire Alert',
				response: '',
				warningMessage: '',
				courseSelectionFrontPage:'',
				iframePrintURL:''
			}
		},
		computed: {
			...mapGetters(['selectedStudent', 'roleRules']),
			selectedAlts : {
				get: function() {
					let courses = [];
					this.elective.forEach(itm=>{
						itm.COURSES.forEach(itm2=>{
							if(itm2.IS_ALTERNATE_SELECTED === 'Y'){
								courses.push(itm2);
							}
						})
					});

					return courses.sort(function( a, b ) {
						if ( a.ALTERNATE_WEIGHT > b.ALTERNATE_WEIGHT ){
							return -1;
						}
						if ( a.ALTERNATE_WEIGHT < b.ALTERNATE_WEIGHT ){
							return 1;
						}
						if ( a.COURSE_ID < b.COURSE_ID ){
							return -1;
						}
						if ( a.COURSE_ID > b.COURSE_ID ){
							return 1;
						}
							return 0;
						}
					);
				},
				set: function(newValue) {
					let index = 1;
					newValue.forEach(itm=>{
						itm.ALTERNATE_WEIGHT = newValue.length - index;
						index++;
					});
					this.$forceUpdate()
				}
				
			},
			creditCount() {
				let total = 0;

				// Mandatory
				for(let m in this.mandatory) {
					let department = this.mandatory[m];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							total += +course.CREDITS
						}
					}
				}

				// Required
				let departmentsWithCourseSel = 0;
				let hasSpecialReq = false;
				let hasSpecialElectiveReq = false;
				for(let r in this.required) {
					let department = this.required[r];
					let hasCourse = false;
					if(department.SPECIAL_REQUEST.IS_SELECTED === "Y"){
						hasSpecialReq = true;
					}
					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							total += +course.CREDITS;
							hasCourse = true;
						}
					}
					
					if(hasCourse || department.SPECIAL_REQUEST.IS_SELECTED === "Y"){
						departmentsWithCourseSel++;
					}
				}

				// Elective
				for(let e in this.elective) {
					let department = this.elective[e];

					if(department.SPECIAL_REQUEST.IS_SELECTED === 'Y') {
						department.SHOW = true;
						hasSpecialElectiveReq = true;
					}

					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y' || course.IS_ALTERNATE_SELECTED === 'Y') {
							department.SHOW = true;
						}
						

						if(course.IS_SELECTED === 'Y' && course.IS_ALTERNATE_SELECTED === 'N') {
							total += +course.CREDITS;
						}
					}
				}

				if(total > this.selectedCourseForm.MAXIMUM_CREDITS) {
					this.warningMessage = this.$t( 'MAIN_CONTENT.CourseRequestForms.maximumRequirementsMet', { max: this.selectedCourseForm.MAXIMUM_CREDITS, total: total } );
				} 
				/* Skip min credits if a special req was selected and each department has at least one thing selected */
				else if (total < this.selectedCourseForm.MINIMUM_CREDITS && !( hasSpecialElectiveReq || (hasSpecialReq && departmentsWithCourseSel === this.required.length) )) {
					this.warningMessage = this.$t( 'MAIN_CONTENT.CourseRequestForms.minimumCreditsNotMet', { min: this.selectedCourseForm.MINIMUM_CREDITS, total: total } );
				} 
				else {
					this.warningMessage = '';
				}

				return total;
			},
			modalContent() {
				let messages = [];

				for(let d in this.required) {
					let department = this.required[d];
					let min = +department.MINIMUM_REQ_COURSES;
					let max = +department.MAXIMUM_REQ_COURSES;
					let selectedCourses = 0;
					/* Special req counts as a course */
					if(department.SPECIAL_REQUEST.IS_SELECTED === 'Y'){
						selectedCourses++;
					}
					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							selectedCourses++;
						}
					}

					if(selectedCourses < min) {
						messages.push(
							this.$t( 'MAIN_CONTENT.CourseRequestForms.youNeedToChoose', { min: min, department: department.DEPARTMENT_DESC, courses: selectedCourses } )
						);
					}
				}

				return messages;
			}
		},
		components: {
			Multiselect,
			Mandatory,
			Required,
			Elective,
			Modal,
			Draggable
		},
		methods: {
			selectedCourseFormText({FORM_DESC}) {
				return `${FORM_DESC}`;
			},
			changeShow(department) {
				for(let i in this.elective) {
					let homeDept = this.elective[i];

					if(homeDept.DEPARTMENT_ID === department.DEPARTMENT_ID) {
						homeDept.SHOW = !homeDept.SHOW;
					}
				}
			},
			loadAllGrades() {
				const self = this;

				const courseRequest = new CourseRequest({
					route: 'course-request-forms',
					query: {
						student_rid: this.selectedStudent.STUDENT_RID
					}
				});

				const promise = courseRequest.get();

				promise.then(response => {
					self.$set(self, 'courseRequestList', response.data.data.forms);
					self.$set(self, 'courseSelectionFrontPage', response.data.data.courseSelectionFrontPage);
					if(response.data.data.forms.length > 0) {
						self.$set(self, 'selectedCourseForm', response.data.data.forms[0]);
					}
				});
			},
			loadCoursesForGrade() {
				const self = this;

				const courseRequest = new CourseRequest({
					route: '/course-request-forms/form',
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
						form_id: this.selectedCourseForm.FORM_ID
					}
				});

				const promise = courseRequest.get();

				return promise.then(response => {
					let mandatory = response.data.data.MANDATORY;
					let required = response.data.data.REQUIRED;
					let elective = response.data.data.ELECTIVE;

					for(let m in mandatory) {
						let department =  mandatory[m];

						for(let d in department.COURSES) {
							let course = department.COURSES[d];

							course.DISPLAY_TITLE = `${course.COURSE_ID} - ${course.COURSE_DESC}`;
						}
					}

					for(let r in required) {
						let department = required[r];
						let count = 0;

						for(let d in department.COURSES) {
							let course = department.COURSES[d];

							course.DISPLAY_TITLE = `${course.COURSE_ID} - ${course.COURSE_DESC}`;
							if(course.IS_SELECTED === 'Y') {
								count++;
							}
						}

						department.MAX_MET = count < department.MAXIMUM_REQ_COURSES ? 'N' : 'Y';
						department.SELECTED_COUNT = count;
					}

					for(let e in elective) {
						let department = elective[e];
						if(this.selectedCourseForm.ELECTIVE_START_HIDDEN_FLG !== "Y"){
							department.SHOW = true;
						}
						for(let d in department.COURSES) {
							let course = department.COURSES[d];

							course.DISPLAY_TITLE = `${course.COURSE_ID} - ${course.COURSE_DESC}`;
						}
					}

					self.$set(self, 'mandatory', mandatory);
					self.$set(self, 'required', required);
					self.$set(self, 'elective', elective);
				});
			},
			setChangedData(type, department, course) {
				this.dataAltered = true;

				if(type === 'required') {
					this.updateSelectedCount(department);
				}

				this.checkPrereqCourses();
			},
			saveCourseRequestForm() {
				const self = this;
				/* reset messages on submit */
				self.$set(this, 'response', '');
				/* If no alts then make sure none are seleected as alts */
				if(this.selectedCourseForm.USE_ALTERNATES_FLG === 'N'){
					self.elective.forEach((itm=>{
						itm.COURSES.forEach(crs=>{
							crs.IS_ALTERNATE_SELECTED = 'N';
						})
					}))
				}
				if(self.modalContent.length === 0) {
					const courseRequest = new CourseRequest({
						route: '/course-request-forms/form',
						query: {
							student_rid: this.selectedStudent.STUDENT_RID,
							form_id: this.selectedCourseForm.FORM_ID
						},
						data: {
							MANDATORY:	self.mandatory,
							REQUIRED:		self.required,
							ELECTIVE:		self.elective
						}
					});

					const promise = courseRequest.post();

					promise.then(response => {
						if(!courseRequest.getResponseData().error) {
							self.dataAltered = false;
							self.$set(this, 'response', courseRequest.getResponse().data);
							window.scrollTo(0, 0);
							self.loadCoursesForGrade().then(()=>{
								/* do print action */
								if(this.response.messages[0].indexOf("Successfully submitted course request form.") > -1){
									this.$refs.printForm.submit();
								}
								
							})
						}
					})
					.catch(error => {
						console.log(error);
					});
				} else {
					self.showModal = true;
				}
			},
			updateSelectedCount(department) {
				let max = department.MAXIMUM_REQ_COURSES;
				let count = 0;

				for(let c in department.COURSES) {
					let course = department.COURSES[c];

					if(course.IS_SELECTED === 'Y') {
						count++;
					}
				}

				department.SELECTED_COUNT = count;

				department.MAX_MET = count < max ? 'N' : 'Y';
			},
			getSelectedCourses() {
				let selectedCourses = [];

				// elective
				for(let d in this.elective) {
					let department = this.elective[d];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							selectedCourses.push(course.COURSE_ID);
						}
					}
				}

				// mandatory
				for(let d in this.mandatory) {
					let department = this.mandatory[d];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							selectedCourses.push(course.COURSE_ID);
						}
					}
				}

				// required
				for(let d in this.required) {
					let department = this.required[d];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];

						if(course.IS_SELECTED === 'Y') {
							selectedCourses.push(course.COURSE_ID);
						}
					}
				}

				return selectedCourses;
			},
			checkPrereqCourses() {
				const self = this;

				const courseRequest = new CourseRequest({
					route: 'course-request-forms/failed-prerequisites',
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
						form_id: this.selectedCourseForm.FORM_ID,
						course_ids: this.getSelectedCourses().join(',')
					}
				});

				const promise = courseRequest.get();
				promise.then(response => {
					let prereqs = response.data.data;
					if( self.selectedCourseForm.PULL_SMS_PREREQS_FLG === 'Y' ){
						self.checkRequiredPrereqs(prereqs);
						self.checkElectivePrereqs(prereqs);
					}
				})
				.catch(error => {
					console.log(error);
				});
			},
			checkRequiredPrereqs(prereqs) {
				for(let d in this.required) {
					let department = this.required[d];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];
						if( prereqs.find( (prereq) => prereq.COURSE_ID.toString() === course.COURSE_ID.toString() ) && !(course.LINE_TYPE === 'SIS_STUD_REC' || course.LINE_TYPE === 'SIS_REQ') ){
							if(this.selectedCourseForm.OVERRIDE_REQ_PREREQS_FLG === 'Y' ) {
								course.IS_SELECTABLE = 'Y';
							} else if( this.selectedCourseForm.REQUESTED_PREREQS_COUNT_FLG === 'N' && course.IS_SELECTED === 'Y' && course.LINE_TYPE === 'FAILED_PREREQ'){
								course.IS_SELECTED = 'N';
							} else if( course.LINE_TYPE === '' ){
								course.IS_SELECTABLE = 'N';
								course.IS_SELECTED = 'N';
								course.IS_ALTERNATE_SELECTED = 'N';
							} else if( course.LINE_TYPE === 'NORMAL' ){
								course.IS_SELECTABLE = 'N';
								course.IS_SELECTED = 'N';
								course.IS_ALTERNATE_SELECTED = 'N';
								course.LINE_TYPE = '';
							} else {
								course.IS_SELECTABLE = 'N';
							}

							if( course.LINE_TYPE === '' ){
								course.LINE_TYPE = 'FAILED_PREREQ';
								this.checkPrereqCourses();
							}
						} else if(course.LINE_TYPE === 'FAILED_PREREQ') {
							course.IS_SELECTABLE = 'Y';
							course.LINE_TYPE = '';
						}

					}
				}
			},
			checkElectivePrereqs(prereqs) {
				for(let d in this.elective) {
					let department = this.elective[d];

					for(let c in department.COURSES) {
						let course = department.COURSES[c];
						if( prereqs.find( (prereq) => prereq.COURSE_ID.toString() === course.COURSE_ID.toString() ) ){
							if(this.selectedCourseForm.OVERRIDE_REQ_PREREQS_FLG === 'Y') {
								course.IS_SELECTABLE = 'Y';
							} else if( this.selectedCourseForm.REQUESTED_PREREQS_COUNT_FLG === 'N' && course.IS_SELECTED === 'Y' && course.LINE_TYPE === 'FAILED_PREREQ'){
								course.IS_SELECTED = 'N';
							} else if( course.LINE_TYPE === '' ){
								course.IS_SELECTABLE = 'N';
								course.IS_SELECTED = 'N';
								course.IS_ALTERNATE_SELECTED = 'N';
							} else if( course.LINE_TYPE === 'NORMAL' ){
								course.IS_SELECTABLE = 'N';
								course.IS_SELECTED = 'N';
								course.IS_ALTERNATE_SELECTED = 'N';
								course.LINE_TYPE = '';
							} else {
								course.IS_SELECTABLE = 'N';
							}

							if( course.LINE_TYPE === '' ){
								course.LINE_TYPE = 'FAILED_PREREQ';
								this.checkPrereqCourses();
							}
						} else if(course.LINE_TYPE === 'FAILED_PREREQ') {
							course.IS_SELECTABLE = 'Y';
							course.LINE_TYPE = '';
						} else {
							course.IS_SELECTABLE = 'Y';
						}

					}
				}
			},
			closeModal() {
				this.showModal = false;
			}
		},
		created() {
			this.$store.commit('setTitleIcon', 'edit');
			this.$store.commit('setTitle', 'MAIN_CONTENT.CourseRequestForms.selectCourseRequestForm');
			this.$store.commit('setUseHeader', true);

			if(this.roleRules.COURSE_REQUESTS_FLG === 'Y') {
				this.loadAllGrades();
			} else {
				this.$router.push({ name: 'HomePage' });
			}
		}
	}
</script>
